import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { hasSeenBroadcastTour, markBroadcastTourSeen } from '../../utils/broadcastTourSeen';
import './BroadcastTour.css';

export type TourTarget = 'map' | 'timeline' | 'search' | 'standings';

interface TourStep {
  target: TourTarget;
  title: string;
  body: string;
  cta?: string;
}

const STEPS: TourStep[] = [
  {
    target: 'map',
    title: 'County colors are empires',
    body: 'Every county on the map belongs to a team. Watch the nation change as the season plays out.',
  },
  {
    target: 'timeline',
    title: 'Scrub the season',
    body: 'Use the arrows to step day by day, or drag the knob to jump — tip-off through the title game.',
  },
  {
    target: 'search',
    title: 'Who owns your county?',
    body: 'Search a county name or ZIP. Jump straight to your turf and see which empire holds it.',
  },
  {
    target: 'standings',
    title: 'Wins seize everything',
    body: 'When a team wins, they take all of their opponent’s land — not a slice. Tap a row to focus an empire.',
    cta: 'Start exploring',
  },
];

const PAD = 8;

type Rect = { top: number; left: number; width: number; height: number };

function readTargetRect(target: TourTarget): Rect | null {
  // Map fills the viewport — spotlight the safe band between topbar and lower third.
  if (target === 'map') {
    const narrow = window.innerWidth < 860;
    const top = narrow ? 64 : 78;
    const bottom = narrow ? 168 : 188;
    const side = narrow ? 10 : 18;
    return {
      top,
      left: side,
      width: Math.max(40, window.innerWidth - side * 2),
      height: Math.max(80, window.innerHeight - top - bottom),
    };
  }

  const nodes = document.querySelectorAll(`[data-tour="${target}"]`);
  let best: DOMRect | null = null;
  for (const node of Array.from(nodes)) {
    const el = node as HTMLElement;
    const style = window.getComputedStyle(el);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') continue;
    const r = el.getBoundingClientRect();
    if (r.width < 2 || r.height < 2) continue;
    if (!best || r.width * r.height > best.width * best.height) best = r;
  }
  if (!best) return null;
  return {
    top: Math.max(0, best.top - PAD),
    left: Math.max(0, best.left - PAD),
    width: Math.min(window.innerWidth, best.width + PAD * 2),
    height: Math.min(window.innerHeight, best.height + PAD * 2),
  };
}

function forceTourFromUrl(): boolean {
  try {
    return new URLSearchParams(window.location.search).get('tour') === '1';
  } catch {
    return false;
  }
}

export interface BroadcastTourProps {
  /** Map engine + ownership ready — delay auto-start until then. */
  ready?: boolean;
  /** Hide when chrome is hidden for capture. */
  disabled?: boolean;
  /** External open request (Independent panel / reopen). */
  openRequest?: number;
  onStandingsReveal?: () => void;
  onSearchReveal?: () => void;
}

const BroadcastTour: React.FC<BroadcastTourProps> = ({
  ready = false,
  disabled = false,
  openRequest = 0,
  onStandingsReveal,
  onSearchReveal,
}) => {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);
  const [hole, setHole] = useState<Rect | null>(null);
  const [cardPos, setCardPos] = useState<'bottom' | 'top'>('bottom');
  const cardRef = useRef<HTMLDivElement>(null);
  const startedRef = useRef(false);

  const finish = useCallback(() => {
    markBroadcastTourSeen();
    setOpen(false);
  }, []);

  const openTour = useCallback((fromStep = 0) => {
    setStep(fromStep);
    setOpen(true);
  }, []);

  // Auto-start once for first-time visitors (or ?tour=1).
  useEffect(() => {
    if (!ready || disabled || startedRef.current) return;
    const forced = forceTourFromUrl();
    if (!forced && hasSeenBroadcastTour()) return;
    startedRef.current = true;
    const t = window.setTimeout(() => openTour(0), forced ? 200 : 700);
    return () => window.clearTimeout(t);
  }, [ready, disabled, openTour]);

  useEffect(() => {
    if (openRequest > 0) {
      startedRef.current = true;
      openTour(0);
    }
  }, [openRequest, openTour]);

  const current = STEPS[step];

  // Reveal mobile panels when those steps are active.
  useEffect(() => {
    if (!open) return;
    if (current.target === 'standings') onStandingsReveal?.();
    if (current.target === 'search') onSearchReveal?.();
  }, [open, current.target, onStandingsReveal, onSearchReveal]);

  const measure = useCallback(() => {
    if (!open) return;
    const rect = readTargetRect(STEPS[step].target);
    setHole(rect);
    if (!rect) {
      setCardPos('bottom');
      return;
    }
    const spaceBelow = window.innerHeight - (rect.top + rect.height);
    setCardPos(spaceBelow < 200 && rect.top > 220 ? 'top' : 'bottom');
  }, [open, step]);

  useLayoutEffect(() => {
    measure();
  }, [measure]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => measure();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
    };
  }, [open, measure]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        finish();
      } else if (e.key === 'ArrowRight' && step < STEPS.length - 1) {
        e.preventDefault();
        setStep((s) => s + 1);
      } else if (e.key === 'ArrowLeft' && step > 0) {
        e.preventDefault();
        setStep((s) => s - 1);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, step, finish]);

  useEffect(() => {
    if (open) {
      window.setTimeout(() => cardRef.current?.querySelector<HTMLButtonElement>('.bc-tour__next')?.focus(), 40);
    }
  }, [open, step]);

  if (!open || disabled) return null;

  const isLast = step === STEPS.length - 1;
  const holeStyle = hole
    ? {
        top: hole.top,
        left: hole.left,
        width: hole.width,
        height: hole.height,
      }
    : undefined;

  return (
    <div className="bc-tour" role="dialog" aria-modal="true" aria-labelledby="bc-tour-title">
      <button
        type="button"
        className={`bc-tour__scrim${hole ? '' : ' bc-tour__scrim--dim'}`}
        aria-label="Skip tour"
        onClick={finish}
      />
      {hole && <div className="bc-tour__hole" style={holeStyle} aria-hidden />}
      <div
        ref={cardRef}
        className={`bc-tour__card bc-tour__card--${cardPos}${hole ? '' : ' bc-tour__card--solo'}`}
      >
        <div className="bc-tour__chrome">
          <span className="bc-tour__plate">
            <span className="bc-tour__stars" aria-hidden>
              ★★
            </span>
            TOUR
          </span>
          <span className="bc-tour__progress" aria-live="polite">
            {step + 1} / {STEPS.length}
          </span>
          <button type="button" className="bc-tour__close" onClick={finish} aria-label="Close tour">
            ✕
          </button>
        </div>
        <h2 id="bc-tour-title" className="bc-tour__title">
          {current.title}
        </h2>
        <p className="bc-tour__body">{current.body}</p>
        <div className="bc-tour__actions">
          <button type="button" className="bc-tour__skip" onClick={finish}>
            Skip
          </button>
          <div className="bc-tour__nav">
            <button
              type="button"
              className="bc-tour__back"
              disabled={step === 0}
              onClick={() => setStep((s) => Math.max(0, s - 1))}
            >
              Back
            </button>
            {isLast ? (
              <button type="button" className="bc-tour__next" onClick={finish}>
                {current.cta || 'Got it'}
              </button>
            ) : (
              <button type="button" className="bc-tour__next" onClick={() => setStep((s) => s + 1)}>
                Next
              </button>
            )}
          </div>
        </div>
        <div className="bc-tour__dots" role="tablist" aria-label="Tour steps">
          {STEPS.map((s, i) => (
            <button
              key={s.target}
              type="button"
              role="tab"
              aria-selected={i === step}
              aria-label={`Step ${i + 1}: ${s.title}`}
              className={`bc-tour__dot${i === step ? ' on' : ''}`}
              onClick={() => setStep(i)}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default BroadcastTour;
